/**
 * Dashboard (ARCHITECTURE.md §8) — the at-a-glance overview: portfolio value over
 * time (with a `?period=` switch), the profit-vs-new-money decomposition, the
 * stocks/cash allocation donut, and a per-strategy P&L grid. All server state is
 * TanStack Query against the typed `hc<AppType>` client; the selected period is a
 * typed route search param so it survives reload and deep-links. Every panel has a
 * real loading / error / empty state — a fresh install (no account/snapshot) shows
 * honest empties, never a wall of errors.
 */
import { useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useI18n } from "@richrich/i18n/react";
import {
  AllocationDonut,
  DecompositionPanel,
  PortfolioValueChart,
  StrategyPnLGrid,
} from "@richrich/ui";
import type { PortfolioPeriod } from "@richrich/contracts";
import { settingsQuery } from "../lib/query";
import {
  DEFAULT_BASE_CURRENCY,
  DEFAULT_PERIOD,
  allocationQuery,
  decompositionQuery,
  emptyAllocation,
  emptyDecomposition,
  emptyTimeseries,
  parsePeriodSearch,
  periodToRange,
  summaryQuery,
  timeseriesQuery,
} from "../features/dashboard/api";
import { useStrategyPnlGrid } from "../features/dashboard/useStrategyPnl";
import { ErrorPanel } from "../features/dashboard/panels";

export const Route = createFileRoute("/")({
  validateSearch: (search: Record<string, unknown>): { period?: PortfolioPeriod } =>
    parsePeriodSearch(search.period),
  loaderDeps: ({ search }) => ({ period: search.period ?? DEFAULT_PERIOD }),
  loader: ({ context, deps }) => {
    const { queryClient } = context;
    const { from, to } = periodToRange(deps.period);
    // Non-blocking cache warm — panels still own their loading states.
    void queryClient.prefetchQuery(timeseriesQuery(deps.period));
    void queryClient.prefetchQuery(decompositionQuery(from, to));
    void queryClient.prefetchQuery(allocationQuery);
    void queryClient.prefetchQuery(summaryQuery);
  },
  component: DashboardPage,
});

function DashboardPage() {
  const { t } = useI18n();
  const search = Route.useSearch();
  const period = search.period ?? DEFAULT_PERIOD;
  const navigate = useNavigate({ from: Route.fullPath });

  const setPeriod = (next: PortfolioPeriod) =>
    void navigate({ search: (prev) => ({ ...prev, period: next }) });

  const { from, to } = useMemo(() => periodToRange(period), [period]);

  const settings = useQuery(settingsQuery);
  const baseCurrency = settings.data?.baseCurrency ?? DEFAULT_BASE_CURRENCY;

  const timeseries = useQuery(timeseriesQuery(period));
  const decomposition = useQuery(decompositionQuery(from, to));
  const allocation = useQuery(allocationQuery);
  const strategies = useStrategyPnlGrid(baseCurrency);

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6 px-8 py-7">
      <header className="border-b border-hairline pb-5">
        <div className="mb-1.5 font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-muted">
          {t("dashboard.overviewEyebrow")}
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">{t("nav.dashboard")}</h1>
        <p className="mt-1.5 max-w-2xl text-sm text-ink-secondary">{t("dashboard.description")}</p>
      </header>

      {timeseries.isError ? (
        <ErrorPanel
          title={t("dashboard.panelPortfolioValue")}
          error={timeseries.error}
          onRetry={() => timeseries.refetch()}
        />
      ) : (
        <PortfolioValueChart
          data={timeseries.data ?? emptyTimeseries(period, baseCurrency)}
          period={period}
          onPeriodChange={setPeriod}
          loading={timeseries.isPending}
          height={300}
        />
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          {decomposition.isError ? (
            <ErrorPanel
              title={t("dashboard.panelDecomposition")}
              error={decomposition.error}
              onRetry={() => decomposition.refetch()}
              height={340}
            />
          ) : (
            <DecompositionPanel
              data={decomposition.data ?? emptyDecomposition(from, to, baseCurrency)}
              loading={decomposition.isPending}
            />
          )}
        </div>
        <div>
          {allocation.isError ? (
            <ErrorPanel
              title={t("dashboard.panelAllocation")}
              error={allocation.error}
              onRetry={() => allocation.refetch()}
              height={260}
            />
          ) : (
            <AllocationDonut
              data={allocation.data ?? emptyAllocation(baseCurrency)}
              loading={allocation.isPending}
            />
          )}
        </div>
      </div>

      {strategies.isError ? (
        <ErrorPanel
          title={t("dashboard.panelStrategyPnl")}
          error={t("dashboard.strategyPnlLoadError")}
          onRetry={strategies.refetch}
        />
      ) : (
        <StrategyPnLGrid items={strategies.items} loading={strategies.isLoading} />
      )}
    </div>
  );
}
